import { useContext, useId, useLayoutEffect, useMemo, useRef, type RefObject } from 'react'
import { HullScopeContext } from './hullScope'

const NONE: ReadonlySet<string> = new Set()

export interface SelectionHullRowOptions {
  /** Kennung der Zeile, z. B. die des Abschnitts. */
  id: string
  /** Sieht markiert aus — fokussiert oder in der Auswahl. */
  selected: boolean
  elRef: RefObject<HTMLElement | null>
  /**
   * Misst die Zeile, wo `getBoundingClientRect` nicht ihr Platz ist: Ein angehefteter Kopf steht woanders
   * als im Fluss.
   */
  measure?: (el: HTMLElement) => DOMRect
  /** Gar nicht melden (archiviert, Zug-Bild): Die Zeile malt ihre Markierung selbst, wie ohne Hülle. */
  disabled?: boolean
}

/**
 * Wer die Markierung malt: die Hülle (`'painting'`), das Element selbst, weil die Seite gerade ruht
 * (`'resting'`, ein Abschnitt wird gezogen — es malt dann in der Form der Hülle), oder es gibt keine Hülle
 * (`null`).
 */
export type SelectionHullRowState = 'painting' | 'resting' | null

/**
 * **Meldet ein einzelnes Element als Zeile bei der Hülle der Seite** (#293, Stufe 4) — für Abschnittsköpfe
 * zwischen den Aufgabenlisten. Dieselbe Fläche fährt von der Aufgabe auf den Kopf und zurück, statt dass der
 * Kopf sich daneben selbst einfärbt (Sven, 09.10.2026: „man merkt, dass es zwei verschiedene Systeme sind“).
 *
 * Sagt, wer die Markierung malt (`SelectionHullRowState`). Malt die Hülle, malt das Element seine
 * Markierung nicht selbst. Ein Block reicht nie über das Element hinaus.
 *
 * @example
 * const hull = useSelectionHullRow({ id, selected: focused || selected, elRef: rootRef, measure: inFlowRect })
 * const inHull = hull === 'painting'
 */
export function useSelectionHullRow({ id, selected, elRef, measure, disabled = false }: SelectionHullRowOptions): SelectionHullRowState {
  const scope = useContext(HullScopeContext)
  const store = scope?.store
  const sourceId = useId()
  const rowIds = useMemo(() => [id], [id])
  const inIds = useMemo(() => (selected ? new Set([id]) : NONE), [selected, id])
  const measureRef = useRef(measure)
  measureRef.current = measure

  useLayoutEffect(() => {
    if (!store || disabled) return
    store.set(sourceId, {
      el: elRef.current,
      rowIds,
      inIds,
      excludedId: null,
      selectionKey: selected ? id : '',
      suspended: false,
      measureSelf: () => {
        const el = elRef.current
        if (!el) return null
        return measureRef.current ? measureRef.current(el) : el.getBoundingClientRect()
      },
    })
  }, [store, disabled, sourceId, elRef, rowIds, inIds, selected, id])
  useLayoutEffect(() => {
    if (!store || disabled) return
    return () => store.delete(sourceId)
  }, [store, disabled, sourceId])

  if (!scope || disabled) return null
  return scope.resting ? 'resting' : 'painting'
}
